import { useMutation } from '@apollo/client';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import { AlertDismissible } from '@website/components/utilities/AlertDismissible';
import ErrorPage from '@website/components/utilities/ErrorPage';
import {
  TripDetailsForTripEditFormQuery,
  UpdateTripDetailsDocument,
  UpdateTripInput,
} from '@website/types/graphqlOperations';
import Router from 'next/router';
import { useCallback, useState } from 'react';
import { Col, Form, FormGroup, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

type NBNoticesFormProps = {
  trip: TripDetailsForTripEditFormQuery['tripByURL'];
};

export type NBNoticesFormType = {
  notice?: string;
  allowPrivateGroups?: boolean;
};

export default function NBNoticesForm({ trip }: NBNoticesFormProps) {
  const [successMessage, setSuccessMessage] = useState<string>();
  const methods = useForm<NBNoticesFormType>({
    defaultValues: {
      allowPrivateGroups: trip?.allowPrivateGroups,
      notice: trip?.notice,
    },
  });

  const [updateTripFN, { data, error }] = useMutation(
    UpdateTripDetailsDocument,
  );

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = useCallback(
    async (formData: NBNoticesFormType) => {
      const updateTrip: UpdateTripInput = {
        id: trip.id,
        active: trip.active === 'ACTIVE',
        address: trip.address,
        description: trip.description,
        endTimezone: trip.endTimezone,
        hostID: trip.host.id,
        image: {
          id: trip.image.id,
        },
        startTimezone: trip.startTimezone,
        title: trip.title,
        urlSlug: trip.urlSlug,
        activities: trip.activities,
        difficultyLevel: trip.difficultyLevel,
        tripVideoURL: trip.tripVideoURL,
        allowPrivateGroups: formData.allowPrivateGroups,
        tripVideoURLThumbnail: trip.tripVideoURLThumbnail
          ? {
              id: trip.tripVideoURLThumbnail?.id,
            }
          : undefined,
        includes: trip.includes,
        featuredImages: trip.featuredImages?.map((image) => ({
          id: image.id,
        })),
        meals: trip.meals,
        excludes: trip.excludes,
        notice: formData.notice,
      };

      await updateTripFN({
        variables: {
          input: updateTrip,
        },
      });

      setSuccessMessage('Trip updated successfully');
    },
    [trip, updateTripFN],
  );

  const onDismissAlert = () => {
    // redirect only new trips
    if (!trip || !trip.id) {
      Router.push(`/trips/${data?.updateTrip.urlSlug}`);
    }
  };

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      {successMessage && (
        <Row>
          <Col>
            <AlertDismissible
              onClose={() => onDismissAlert()}
              variant="success"
              title="Trip Edit Form"
              message={successMessage}
            />
          </Col>
        </Row>
      )}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-3 leading">NB Notices</div>
        <FormGroup controlId="notice" className="mb-3">
          <Form.Label className="d-block">
            If there is something very specific and important that guests need
            to know about your trip, please write it here.
          </Form.Label>

          <Form.Control
            as="textarea"
            rows={6}
            placeholder="Enter notice"
            {...methods.register('notice')}
          />
        </FormGroup>

        <Form.Group controlId="allowPrivateGroups" className="mb-3">
          <Form.Check
            type="checkbox"
            label="Include a message about opening new dates for 2 or more people."
            {...methods.register('allowPrivateGroups')}
            defaultChecked={trip.allowPrivateGroups ?? false}
          />
        </Form.Group>

        <SubmitButton isSubmitting={isSubmitting} />
      </Form>
    </>
  );
}
